import React, { ReactElement } from 'react';
import { GridCellParams } from '@mui/x-data-grid';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import {
  FileCopyOutlined,
  OndemandVideo,
  PeopleOutlineOutlined,
  EditOutlined,
  MoreVert,
} from '@mui/icons-material';
import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined';
import { classes } from '@components/containers/AdGroupsIndexPage/styles';
import { useAdGroupsTable } from '@components/containers/AdGroupsIndexPage/AdGroupsTableContext';
import { renderStatus } from '@components/containers/AdGroupsIndexPage/AdGroupsTable';
import { LineItem } from '@local-types';
import { renderArchiveUnarchiveStatus } from '@components/containers/AdGroupsIndexPage/utils';
import { Statuses } from '@components/containers/AdGroupsIndexPage/statuses';
import { useFlags } from '@components/hooks';

export const AdGroupActionMenuCellRenderer = (
  params: GridCellParams<LineItem>,
) => {
  const { id, url, is_control_group, active, status } = params.row;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const {
    handleManageDisplay,
    handleManageAdGroup,
    handleIsDuplicatable,
    handleDuplicateAdGroup,
    handleOpenWeighting,
    handleOpenBid,
    handlePauseActive,
    handleArchiveUnarchiveAdGroup,
  } = useAdGroupsTable();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { flags } = useFlags();
  const canArchive =
    flags.USER_CAN_ARCHIVE &&
    (status === Statuses.PAUSED || status === Statuses.ARCHIVED);

  const isDisplay = url.includes('static');

  let children: ReactElement[] = [
    <MenuItem
      data-testid="manage-display"
      key={`manage-display-${id}`}
      onClick={() => {
        handleManageDisplay(params.row);
      }}
      sx={{ display: 'flex', gap: 2 }}
    >
      <AspectRatioOutlinedIcon
        fontSize="small"
        className={classes.menuIcon}
      />
      Manage Display
    </MenuItem>,
  ];

  if (!isDisplay) {
    children = [
      status !== Statuses.ARCHIVED && (
        <MenuItem
          data-testid="manage-ad-group"
          key={`manage-ad-group-${id}`}
          onClick={() => {
            handleClose();
            handleManageAdGroup(params.row);
          }}
          sx={{ display: 'flex', gap: 2 }}
        >
          <PeopleOutlineOutlined fontSize="small" />
          Manage Ad Group
        </MenuItem>
      ),
      status !== Statuses.DRAFT && (
        <MenuItem
          data-testid="duplicate-ad-group"
          key={`duplicate-ad-group-${id}`}
          onClick={() => {
            handleClose();
            handleDuplicateAdGroup(params.row);
          }}
          disabled={handleIsDuplicatable(params.row)}
          sx={{ display: 'flex', gap: 2 }}
        >
          <FileCopyOutlined fontSize="small" />
          Duplicate Ad Group
        </MenuItem>
      ),
      status !== Statuses.ARCHIVED && (
        <MenuItem
          data-testid="edit-weighting"
          key={`edit-weighting-${id}`}
          onClick={() => {
            handleOpenWeighting(params.row);
            handleClose();
          }}
          sx={{ display: 'flex', gap: 2 }}
        >
          <OndemandVideo fontSize="small" />
          Edit Weighting
        </MenuItem>
      ),
      !is_control_group &&
        status !== Statuses.DRAFT &&
        status !== Statuses.ARCHIVED && (
          <MenuItem
            data-testid="edit-bid"
            key={`edit-bid-${id}`}
            onClick={() => {
              handleOpenBid(params.row);
              handleClose();
            }}
            sx={{ display: 'flex', gap: 2 }}
          >
            <EditOutlined fontSize="small" />
            Edit Bid
          </MenuItem>
        ),
      !is_control_group &&
        (status === Statuses.ACTIVE || status === Statuses.PAUSED) && (
          <MenuItem
            key={`pause-active-${id}`}
            data-testid="pause-active"
            onClick={() => {
              handlePauseActive(params.row);
              handleClose();
            }}
            sx={{ display: 'flex', gap: 2 }}
          >
            {renderStatus(active, 'Ad Group')}
          </MenuItem>
        ),
      canArchive && (
        <MenuItem
          onClick={() => {
            handleArchiveUnarchiveAdGroup(params.row);
            handleClose();
          }}
          key={`archive-unarchive-${id}`}
          sx={{ display: 'flex', gap: 2 }}
        >
          {renderArchiveUnarchiveStatus(status!, 'Ad Group')}
        </MenuItem>
      ),
    ].filter(Boolean) as ReactElement[];
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Button
        sx={{ color: 'primary.main' }}
        variant="text"
        size="small"
        id={`action-menu-${id}`}
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreVert />
      </Button>
      <Menu
        id={`action-menu-${id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `action-menu-${id}`,
        }}
      >
        {children}
      </Menu>
    </Box>
  );
};
